/**
 * 📥 นำเข้า Q&A — สร้างคู่คำถาม-คำตอบหลายอันทีเดียว
 *  - วางลิสต์: "1) คำถาม: ... คำตอบ: ..." → แยกอัตโนมัติ
 *  - อัปรูป: รูปลิสต์ Q&A → Gemini อ่านออกมาเป็นคู่
 *  → ตาราง preview แก้ได้ → บันทึกเข้า FAQ ทีเดียว
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Trash2, Upload, ListPlus, Save } from 'lucide-react';
import { FaqImportApi, type ParsedQA } from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { useBrands, brandEmoji, brandLabel } from '@/lib/useBrands';

const PLACEHOLDER = `วางลิสต์ที่นี่ เช่น:
1) คำถาม: ส่งกี่วันคะ
คำตอบ: ส่ง 2-3 วันทำการค่ะ
2) คำถาม: มีเก็บเงินปลายทางไหม
คำตอบ: มีค่ะ บวกค่าบริการ 20 บาท`;

/** แยกคู่ Q&A จากข้อความ — รองรับ คำถาม/ถาม/Q + คำตอบ/ตอบ/A, มีเลขหรือไม่ก็ได้ */
function parseQAList(raw: string): ParsedQA[] {
  const out: ParsedQA[] = [];
  const re =
    /(?:^|\n)\s*(?:\d+[).\]]?\s*)?(?:คำถาม|ถาม|q|question)\s*[:：]\s*([\s\S]*?)\s*(?:คำตอบ|ตอบ|a|answer)\s*[:：]\s*([\s\S]*?)(?=(?:\n\s*(?:\d+[).\]]?\s*)?(?:คำถาม|ถาม|q|question)\s*[:：])|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    const question = (m[1] ?? '').trim();
    const answer = (m[2] ?? '').trim();
    if (question && answer) out.push({ question, answer });
  }
  return out;
}

export default function FaqImportPage() {
  const qc = useQueryClient();
  const { keys } = useBrands();
  const [brand, setBrand] = useState('');
  const [mode, setMode] = useState<'paste' | 'image'>('paste');
  const [raw, setRaw] = useState('');
  const [rows, setRows] = useState<ParsedQA[]>([]);
  const [busy, setBusy] = useState(false);
  if (!brand && keys.length) setBrand(keys[0]!);

  function doParsePaste() {
    const parsed = parseQAList(raw);
    if (!parsed.length) {
      toast.error('ไม่พบคู่คำถาม-คำตอบ — ตรวจรูปแบบ "คำถาม:" / "คำตอบ:"');
      return;
    }
    setRows(parsed);
    toast.success(`แยกได้ ${parsed.length} คู่`);
  }

  async function doParseImage(file: File) {
    setBusy(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result));
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      const r = await FaqImportApi.parseImage(dataUrl, file.type || 'image/jpeg');
      if (!r.items.length) {
        toast.error('อ่านรูปไม่พบคู่ Q&A');
        return;
      }
      setRows(r.items);
      toast.success(`อ่านรูปได้ ${r.items.length} คู่`);
    } catch (e) {
      toast.error(`อ่านรูปไม่ได้: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function edit(i: number, k: keyof ParsedQA, v: string) {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  }

  async function save() {
    const valid = rows.filter((r) => r.question.trim() && r.answer.trim());
    if (!valid.length) {
      toast.error('ไม่มีรายการที่ครบคำถาม+คำตอบ');
      return;
    }
    setBusy(true);
    try {
      const r = await FaqImportApi.bulkCreate(brand, valid);
      qc.invalidateQueries({ queryKey: ['faqs'] });
      toast.success(`บันทึก ${r.count} คู่เข้า FAQ แล้ว ✅`);
      setRows([]);
      setRaw('');
    } catch (e) {
      toast.error(`บันทึกไม่ได้: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">📥 นำเข้า Q&amp;A หลายคู่</h2>
        <p className="text-sm text-muted-foreground">วางลิสต์ หรืออัปรูป → ระบบสร้างคู่คำถาม-คำตอบเข้า FAQ ทีเดียว</p>
      </div>

      {/* ชี้ทางไปดู/แก้รายการเดิม — กันสับสนว่าหน้านี้ไม่มี list */}
      <div className="text-sm bg-blue-50 border border-blue-200 rounded-lg p-3">
        💡 หน้านี้ใช้ <b>นำเข้าหลายอันทีเดียว</b> เท่านั้น — <b>รายการคำถาม-คำตอบทั้งหมด (เพิ่ม/แก้/ลบทีละอัน)</b> อยู่ที่{' '}
        <Link to="/settings/answers" className="text-blue-700 font-semibold underline">💬 คำตอบบอท</Link>
      </div>

      {/* แบรนด์ */}
      <div className="flex flex-wrap gap-1.5">
        {keys.map((k) => (
          <button
            key={k}
            onClick={() => setBrand(k)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              brand === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-accent'
            }`}
          >
            {brandEmoji(k)} {brandLabel(k)}
          </button>
        ))}
      </div>

      {/* โหมด */}
      <div className="flex gap-2">
        <Button size="sm" variant={mode === 'paste' ? 'default' : 'outline'} onClick={() => setMode('paste')} className="gap-1">
          <ListPlus size={15} /> วางลิสต์
        </Button>
        <Button size="sm" variant={mode === 'image' ? 'default' : 'outline'} onClick={() => setMode('image')} className="gap-1">
          <Upload size={15} /> อัปรูป
        </Button>
      </div>

      {mode === 'paste' ? (
        <div className="space-y-2">
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={8}
            className="w-full border rounded-lg p-3 text-sm font-mono bg-background"
          />
          <Button size="sm" onClick={doParsePaste} disabled={!raw.trim()}>แยกรายการ</Button>
        </div>
      ) : (
        <div className="border-2 border-dashed rounded-lg p-6 text-center">
          <input
            type="file"
            accept="image/*"
            id="qa-img"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && doParseImage(e.target.files[0])}
          />
          <label htmlFor="qa-img" className="cursor-pointer text-sm text-primary hover:underline">
            {busy ? 'กำลังอ่านรูป…' : '📷 เลือกรูปลิสต์ Q&A (AI อ่านให้)'}
          </label>
          <p className="text-[11px] text-muted-foreground mt-1">รูปแคป/ถ่ายลิสต์คำถาม-คำตอบ ภาษาไทยได้</p>
        </div>
      )}

      {/* preview ตารางแก้ได้ */}
      {rows.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-medium">ตรวจ/แก้ก่อนบันทึก ({rows.length} คู่)</div>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="border rounded-lg p-2 bg-card flex gap-2">
                <span className="text-xs text-muted-foreground pt-2 w-5">{i + 1}.</span>
                <div className="flex-1 space-y-1">
                  <input
                    value={r.question}
                    onChange={(e) => edit(i, 'question', e.target.value)}
                    placeholder="คำถาม"
                    className="w-full border rounded px-2 py-1 text-sm font-medium bg-background"
                  />
                  <textarea
                    value={r.answer}
                    onChange={(e) => edit(i, 'answer', e.target.value)}
                    placeholder="คำตอบ"
                    rows={2}
                    className="w-full border rounded px-2 py-1 text-sm bg-background"
                  />
                  <input
                    value={r.keywords ?? ''}
                    onChange={(e) => edit(i, 'keywords', e.target.value)}
                    placeholder="คีย์เวิร์ด (เว้นว่าง = ดึงจากคำถามอัตโนมัติ)"
                    className="w-full border rounded px-2 py-1 text-[11px] text-muted-foreground bg-background"
                  />
                </div>
                <button onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))} className="p-1 text-red-500 hover:bg-red-50 rounded h-fit">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={busy || !brand} className="gap-2">
            <Save size={16} /> {busy ? 'กำลังบันทึก…' : `บันทึกทั้งหมด (${rows.length})`}
          </Button>
        </div>
      )}
    </div>
  );
}
